import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpc, RouterOutputs } from "@/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type Project = RouterOutputs["projects"]["list"][number];

type ProjectFormState = {
  name: string;
  // Appointments hours
  weekDayStart: string;
  weekDayEnd: string;
  visitOnSaturdayAllowed: boolean;
  saturdayDayStart: string;
  saturdayDayEnd: string;
  visitOnSundayAllowed: boolean;
  sundayDayStart: string;
  sundayDayEnd: string;
  minimumAdvanceHours: string;
};

// Code is the name lowercased, accents removed, and stripped of any non-alphanumeric characters.
// e.g. "Poblado Central" → "pobladocentral", "Torre del Río" → "torredelrio".
const deriveCodeFromName = (name: string): string =>
  name
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const emptyState = (): ProjectFormState => ({
  name: "",
  weekDayStart: "9:00 AM",
  weekDayEnd: "5:30 PM",
  visitOnSaturdayAllowed: true,
  saturdayDayStart: "10:00 AM",
  saturdayDayEnd: "5:30 PM",
  visitOnSundayAllowed: true,
  sundayDayStart: "10:00 AM",
  sundayDayEnd: "5:30 PM",
  minimumAdvanceHours: "0",
});

const stateFromProject = (project: Project): ProjectFormState => ({
  name: project.name ?? "",
  weekDayStart: project.appointmentsHours?.weekDayStart ?? "9:00 AM",
  weekDayEnd: project.appointmentsHours?.weekDayEnd ?? "5:30 PM",
  visitOnSaturdayAllowed: project.appointmentsHours?.visitOnSaturdayAllowed ?? true,
  saturdayDayStart: project.appointmentsHours?.saturdayDayStart ?? "10:00 AM",
  saturdayDayEnd: project.appointmentsHours?.saturdayDayEnd ?? "5:30 PM",
  visitOnSundayAllowed: project.appointmentsHours?.visitOnSundayAllowed ?? true,
  sundayDayStart: project.appointmentsHours?.sundayDayStart ?? "10:00 AM",
  sundayDayEnd: project.appointmentsHours?.sundayDayEnd ?? "5:30 PM",
  minimumAdvanceHours:
    project.appointmentsHours?.minimumAdvanceHours !== undefined
      ? String(project.appointmentsHours.minimumAdvanceHours)
      : "0",
});

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
    <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>
  </div>
);

const Field = ({
  id,
  label,
  children,
  fullWidth,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) => (
  <div className={fullWidth ? "md:col-span-2" : undefined}>
    <Label htmlFor={id} className="mb-1 block text-sm text-gray-600">
      {label}
    </Label>
    {children}
  </div>
);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  merchantId: string;
  editingProject: Project | null;
};

const ProjectFormDialog = ({ isOpen, onClose, merchantId, editingProject }: Props) => {
  const [form, setForm] = useState<ProjectFormState>(emptyState);
  const utils = trpc.useUtils();

  useEffect(() => {
    if (isOpen) {
      setForm(editingProject ? stateFromProject(editingProject) : emptyState());
    }
  }, [isOpen, editingProject]);

  const createProject = trpc.projects.create.useMutation({
    onSuccess: () => {
      utils.projects.list.invalidate({ merchantId });
      toast.success("Proyecto creado");
      onClose();
    },
    onError: (error) => {
      toast.error(error.message || "Error al crear el proyecto");
    },
  });

  const updateProject = trpc.projects.update.useMutation({
    onSuccess: () => {
      utils.projects.list.invalidate({ merchantId });
      toast.success("Proyecto actualizado");
      onClose();
    },
    onError: (error) => {
      toast.error(error.message || "Error al actualizar el proyecto");
    },
  });

  const isSaving = createProject.isLoading || updateProject.isLoading;

  const derivedCode = editingProject ? editingProject.code : deriveCodeFromName(form.name);

  const buildAppointmentsHoursPayload = () => {
    const minAdvanceNumber =
      form.minimumAdvanceHours.trim() === "" ? undefined : Number(form.minimumAdvanceHours);
    if (form.minimumAdvanceHours.trim() !== "" && Number.isNaN(minAdvanceNumber)) {
      toast.error("Horas mínimas de anticipación inválidas");
      return null;
    }

    return {
      weekDayStart: form.weekDayStart.trim() || undefined,
      weekDayEnd: form.weekDayEnd.trim() || undefined,
      visitOnSaturdayAllowed: form.visitOnSaturdayAllowed,
      saturdayDayStart: form.saturdayDayStart.trim() || undefined,
      saturdayDayEnd: form.saturdayDayEnd.trim() || undefined,
      visitOnSundayAllowed: form.visitOnSundayAllowed,
      sundayDayStart: form.sundayDayStart.trim() || undefined,
      sundayDayEnd: form.sundayDayEnd.trim() || undefined,
      minimumAdvanceHours: minAdvanceNumber,
    };
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    const appointmentsHours = buildAppointmentsHoursPayload();
    if (!appointmentsHours) return;

    if (editingProject) {
      await updateProject.mutateAsync({
        merchantId,
        id: editingProject.id,
        name: form.name.trim(),
        appointmentsHours,
      });
    } else {
      const code = deriveCodeFromName(form.name);
      if (!code) {
        toast.error("Nombre inválido", {
          description: "Usa al menos una letra o número.",
        });
        return;
      }
      await createProject.mutateAsync({
        merchantId,
        name: form.name.trim(),
        code,
        appointmentsHours,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingProject ? "Editar proyecto" : "Crear proyecto"}</DialogTitle>
          <DialogDescription>
            {editingProject
              ? "Modifica el nombre y los horarios de visita."
              : "El código se genera automáticamente a partir del nombre."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <Section title="Información básica">
            <Field id="name" label="Nombre *" fullWidth>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Poblado Central"
              />
            </Field>
            <Field id="code" label="Código (autogenerado)" fullWidth>
              <Input
                id="code"
                value={derivedCode}
                readOnly
                disabled
                className="bg-gray-50 font-mono"
              />
              {!editingProject && form.name.trim() !== "" && (
                <p className="mt-1 text-xs text-gray-500">
                  Se genera a partir del nombre en minúsculas y sin espacios.
                </p>
              )}
              {editingProject && (
                <p className="mt-1 text-xs text-gray-500">
                  El código no se puede cambiar después de crear el proyecto.
                </p>
              )}
            </Field>
          </Section>

          <Section title="Horarios para visitas">
            <Field id="weekDayStart" label="Lunes a viernes — Hora inicio">
              <Input
                id="weekDayStart"
                value={form.weekDayStart}
                onChange={(e) => setForm({ ...form, weekDayStart: e.target.value })}
                placeholder="9:00 AM"
              />
            </Field>
            <Field id="weekDayEnd" label="Lunes a viernes — Hora fin">
              <Input
                id="weekDayEnd"
                value={form.weekDayEnd}
                onChange={(e) => setForm({ ...form, weekDayEnd: e.target.value })}
                placeholder="5:30 PM"
              />
            </Field>
            <div className="flex items-center gap-2 md:col-span-2">
              <Checkbox
                id="visitOnSaturdayAllowed"
                checked={form.visitOnSaturdayAllowed}
                onCheckedChange={(checked) =>
                  setForm({ ...form, visitOnSaturdayAllowed: checked === true })
                }
              />
              <Label htmlFor="visitOnSaturdayAllowed" className="text-sm text-gray-700">
                Visitas permitidas los sábados
              </Label>
            </div>
            {form.visitOnSaturdayAllowed && (
              <>
                <Field id="saturdayDayStart" label="Sábado — Hora inicio">
                  <Input
                    id="saturdayDayStart"
                    value={form.saturdayDayStart}
                    onChange={(e) => setForm({ ...form, saturdayDayStart: e.target.value })}
                    placeholder="10:00 AM"
                  />
                </Field>
                <Field id="saturdayDayEnd" label="Sábado — Hora fin">
                  <Input
                    id="saturdayDayEnd"
                    value={form.saturdayDayEnd}
                    onChange={(e) => setForm({ ...form, saturdayDayEnd: e.target.value })}
                    placeholder="5:30 PM"
                  />
                </Field>
              </>
            )}
            <div className="flex items-center gap-2 md:col-span-2">
              <Checkbox
                id="visitOnSundayAllowed"
                checked={form.visitOnSundayAllowed}
                onCheckedChange={(checked) =>
                  setForm({ ...form, visitOnSundayAllowed: checked === true })
                }
              />
              <Label htmlFor="visitOnSundayAllowed" className="text-sm text-gray-700">
                Visitas permitidas los domingos
              </Label>
            </div>
            {form.visitOnSundayAllowed && (
              <>
                <Field id="sundayDayStart" label="Domingo — Hora inicio">
                  <Input
                    id="sundayDayStart"
                    value={form.sundayDayStart}
                    onChange={(e) => setForm({ ...form, sundayDayStart: e.target.value })}
                    placeholder="10:00 AM"
                  />
                </Field>
                <Field id="sundayDayEnd" label="Domingo — Hora fin">
                  <Input
                    id="sundayDayEnd"
                    value={form.sundayDayEnd}
                    onChange={(e) => setForm({ ...form, sundayDayEnd: e.target.value })}
                    placeholder="5:30 PM"
                  />
                </Field>
              </>
            )}
            <Field id="minimumAdvanceHours" label="Horas mínimas de anticipación">
              <Input
                id="minimumAdvanceHours"
                inputMode="numeric"
                value={form.minimumAdvanceHours}
                onChange={(e) => setForm({ ...form, minimumAdvanceHours: e.target.value })}
                placeholder="0"
              />
            </Field>
          </Section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {editingProject ? "Guardar cambios" : "Crear proyecto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectFormDialog;
